/**
 * @file combobox-list.tsx
 * @description Поиск и список пунктов `Combobox` (phase19.31): одна разметка
 * для всплывающего окна (широкий экран) и нижней шторки (телефон).
 *
 * Вынесено из `combobox.tsx`, чтобы оба способа показа рисовали пункты
 * одинаково: группы («Россия», «Другие»), подпись под пунктом, отметка
 * выбранного, пункт «без значения» и кнопка «создать».
 *
 * Фокус в поле поиска ставится вручную через кадр и с `preventScroll`:
 * прежний `autoFocus` срабатывал на монтировании, пока всплывающее окно ещё
 * стояло в (0,0), и браузер прокручивал длинную форму к верху страницы
 * (замечание владельца 2026-10-19, поле «Производитель» в карточке товара).
 */

import { Check, Plus } from "lucide-react";
import { Fragment, useEffect, useRef, type ReactNode } from "react";

import { Input } from "./input";
import type { ComboboxOption } from "./combobox";
import { cn } from "@/lib/utils";

interface Props {
  options: ComboboxOption[];
  value: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  /** Выбран пункт; `null` — пункт «без значения» (`clearLabel`). */
  onPick: (value: string | null) => void;
  searchPlaceholder: string;
  emptyText: string;
  /** Подпись пункта «без значения» первым в списке; не задана — пункта нет. */
  clearLabel?: string;
  createLabel?: string;
  onCreate?: () => void;
  /** Поле поиска крупнее на телефоне (высота поля темы). */
  touch?: boolean;
}

interface ItemProps {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

/** Пункт списка: отметка выбранного слева, текст справа. */
function ComboboxItem({ selected, disabled, onClick, children }: ItemProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        // min-h-13 (52px) — высота пункта с описанием в две строки:
        // тач-цель одинаковая у всех вариантов (дефект QA 2026-09-21).
        "flex min-h-13 w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-low",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected && "bg-surface-low/60",
      )}
    >
      <Check className={cn("size-4 shrink-0", selected ? "text-primary opacity-100" : "opacity-0")} />
      <div className="min-w-0 flex-1">{children}</div>
    </button>
  );
}

export function ComboboxList({
  options,
  value,
  query,
  onQueryChange,
  onPick,
  searchPlaceholder,
  emptyText,
  clearLabel,
  createLabel,
  onCreate,
  touch = false,
}: Props) {
  const searchRef = useRef<HTMLInputElement>(null);
  // Фокус через кадр: окно уже вставлено в документ; `preventScroll` — страница
  // не прокручивается к полю, где бы окно ни стояло в этот момент.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q) || (o.description ?? "").toLowerCase().includes(q))
    : options;
  // Пункт «без значения» — только без поиска: при поиске ищут конкретное значение.
  const showClear = Boolean(clearLabel) && !q;

  return (
    <>
      <div className={cn("shrink-0", touch ? "pb-2" : "p-2")}>
        <Input
          ref={searchRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          className={touch ? undefined : "h-8 text-sm"}
        />
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {showClear ? (
          <ComboboxItem selected={value === null} onClick={() => onPick(null)}>
            <div className="truncate text-on-surface-variant">{clearLabel}</div>
          </ComboboxItem>
        ) : null}
        {filtered.length === 0 && !showClear ? (
          <div className="px-3 py-4 text-center text-sm text-on-surface-muted">{emptyText}</div>
        ) : (
          filtered.map((o, index) => {
            // Заголовок группы — перед первым пунктом группы в отфильтрованном списке.
            const groupStart = o.group && o.group !== filtered[index - 1]?.group ? o.group : null;
            return (
              <Fragment key={o.value}>
                {groupStart ? (
                  <div role="presentation" className="type-caption px-3 pb-1 pt-2 font-medium text-on-surface-variant">
                    {groupStart}
                  </div>
                ) : null}
                <ComboboxItem selected={o.value === value} disabled={o.disabled} onClick={() => onPick(o.value)}>
                  <div className="truncate">{o.label}</div>
                  {o.description ? <div className="truncate text-xs text-on-surface-variant">{o.description}</div> : null}
                </ComboboxItem>
              </Fragment>
            );
          })
        )}
      </div>
      {createLabel && onCreate ? (
        <div className="shrink-0 border-t border-outline-variant p-2">
          <button
            type="button"
            onClick={onCreate}
            className="flex min-h-12 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-primary hover:bg-surface-low"
          >
            <Plus className="size-4" />
            {createLabel}
          </button>
        </div>
      ) : null}
    </>
  );
}
